import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Layout/Header';
import api, { claimsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Receipt, Plus, CheckCircle, XCircle, Clock, DollarSign, Eye, Plane, Stethoscope, Palmtree, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';
import { 
  CLAIM_CATEGORIES, 
  STATUS_COLORS,
  TAB_CATEGORIES,
  TAB_DESCRIPTIONS,
  ClaimCreateModal,
  ClaimActionModal,
  ClaimUploadModal,
  DocumentViewerModal
} from '../components/claims';

// Thumbnail image component with error fallback
const ThumbnailImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);
  
  if (hasError) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-slate-100">
        <ImageIcon className="w-5 h-5 text-slate-400" />
      </div>
    );
  }
  
  return (
    <img 
      src={src}
      alt={alt}
      className="w-full h-full object-cover"
      onError={() => setHasError(true)}
    />
  );
};

const Claims = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'hr', 'verifier'].includes(user?.role);
  const isVerifier = user?.role === 'verifier';
  
  // Main tab state for claim types
  const [activeTab, setActiveTab] = useState('all');
  
  const [claims, setClaims] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  
  // Modal states
  const [showCreate, setShowCreate] = useState(false);
  const [showAction, setShowAction] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [currentAction, setCurrentAction] = useState('');
  const [viewerDoc, setViewerDoc] = useState({ url: '', name: '', type: '' });

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterCategory]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const promises = [
        isAdmin 
          ? claimsAPI.getAll(filterStatus || undefined, filterCategory || undefined)
          : claimsAPI.getMyClaims(filterStatus || undefined),
        claimsAPI.getSummary()
      ];
      
      // Fetch employees if user is admin or verifier (for creating claims on behalf)
      if (isAdmin || isVerifier) {
        const { employeeMasterAPI } = await import('../lib/api');
        promises.push(employeeMasterAPI.getAll({ limit: 500, status: 'active' }));
      }
      
      const results = await Promise.all(promises);
      setClaims(results[0].data);
      setSummary(results[1].data);
      
      if ((isAdmin || isVerifier) && results[2]) {
        // Handle paginated response {data: [...], total: N} or direct array
        const empData = results[2].data?.data || results[2].data || [];
        setEmployees(empData);
      }
    } catch (error) {
      toast.error('Failed to fetch claims');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitClaim = async (claimId) => {
    try {
      await claimsAPI.processAction(claimId, 'submit', {});
      toast.success('Claim submitted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to submit claim');
    }
  };

  const openActionDialog = (claim, action) => {
    setSelectedClaim(claim);
    setCurrentAction(action);
    setShowAction(true);
  };

  const openUploadDialog = (claim) => {
    setSelectedClaim(claim);
    setShowUpload(true);
  };

  // View receipt in Document Viewer modal
  const viewReceipt = useCallback((receiptUrl, claim) => {
    const filename = claim?.receipt_filename || claim?.attachment_name || 'document';
    setViewerDoc({
      url: receiptUrl,
      name: filename,
      type: filename.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/*'
    });
    setShowDocViewer(true);
  }, []);

  const getStatusBadge = (status) => {
    const styles = STATUS_COLORS[status] || 'bg-gray-100 text-gray-700';
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles}`}>
        {status?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getCategoryIcon = (category) => {
    const icons = {
      travel: '✈️', food: '🍽️', accommodation: '🏨', communication: '📱',
      medical: '🏥', office_supplies: '📎', training: '📚', ltc: '🌴',
      reimbursement: '💰', other: '📋'
    };
    return icons[category] || '📋';
  };

  // Filter claims based on active tab
  const getFilteredClaims = () => {
    if (activeTab === 'all') return claims;
    const categories = TAB_CATEGORIES[activeTab] || [];
    if (categories.length === 0) return claims;
    return claims.filter(c => categories.includes(c.category));
  };

  const filteredClaims = getFilteredClaims();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div data-testid="claims-page">
      <Header 
        title="Claims & Reimbursements" 
        subtitle="Submit and track expense claims"
        action={
          (user?.role === 'employee' || user?.role === 'verifier' || user?.role === 'admin') && (
            <Button onClick={() => setShowCreate(true)} data-testid="new-claim-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Claim
            </Button>
          )
        }
      />
      
      <div className="p-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="dashboard-card stat-card stat-accent-primary" data-testid="pending-claims-stat">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Pending</p>
                <p className="text-2xl font-heading font-semibold text-slate-900">
                  {(summary?.by_status?.submitted?.count || 0) + (summary?.by_status?.verified?.count || 0)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-amber-100">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </div>
          
          <div className="dashboard-card stat-card stat-accent-success" data-testid="approved-claims-stat">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Approved</p>
                <p className="text-2xl font-heading font-semibold text-emerald-600">
                  {formatCurrency(summary?.by_status?.approved?.total || 0)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-100">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </div>
          
          <div className="dashboard-card stat-card stat-accent-accent" data-testid="paid-claims-stat">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Paid</p>
                <p className="text-2xl font-heading font-semibold text-green-700">
                  {formatCurrency(summary?.by_status?.paid?.total || 0)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-green-100">
                <DollarSign className="w-5 h-5 text-green-700" />
              </div>
            </div>
          </div>
          
          <div className="dashboard-card stat-card" data-testid="rejected-claims-stat">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Rejected</p>
                <p className="text-2xl font-heading font-semibold text-red-600">
                  {summary?.by_status?.rejected?.count || 0}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-red-100">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="all" className="gap-2">
                <Receipt className="w-4 h-4" />
                All Claims
              </TabsTrigger>
              <TabsTrigger value="reimbursement" className="gap-2">
                <Receipt className="w-4 h-4" />
                Reimbursement
              </TabsTrigger>
              <TabsTrigger value="medical" className="gap-2">
                <Stethoscope className="w-4 h-4" />
                Medical
              </TabsTrigger>
              <TabsTrigger value="travel" className="gap-2">
                <Plane className="w-4 h-4" />
                Travel
              </TabsTrigger>
              <TabsTrigger value="ltc" className="gap-2">
                <Palmtree className="w-4 h-4" />
                LTC
              </TabsTrigger>
            </TabsList>
            
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Select value={filterStatus || 'all_statuses'} onValueChange={(v) => setFilterStatus(v === 'all_statuses' ? '' : v)}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_statuses">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <TabsContent value={activeTab} className="space-y-4">
            {/* Tab Description Banner */}
            {TAB_DESCRIPTIONS[activeTab] && (
              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Claims
                  </p>
                  <p className="text-sm text-blue-600">{TAB_DESCRIPTIONS[activeTab]}</p>
                </div>
              </div>
            )}

            {/* Claims Table */}
            <div className="dashboard-card p-0 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    {isAdmin && <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Employee</th>}
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Category</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Title</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Receipt</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Amount</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredClaims.map(claim => (
                    <tr key={claim.id} className="hover:bg-slate-50" data-testid={`claim-row-${claim.id}`}>
                      {isAdmin && (
                        <td className="py-3 px-4">
                          <p className="font-medium text-sm">{claim.employee_name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500">{claim.employee_code}</p>
                        </td>
                      )}
                      <td className="py-3 px-4">
                        <span className="text-lg mr-2">{getCategoryIcon(claim.category)}</span>
                        <span className="text-sm capitalize">{claim.category?.replace('_', ' ')}</span>
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-medium text-sm">{claim.title}</p>
                        {claim.description && (
                          <p className="text-xs text-slate-500 truncate max-w-xs">{claim.description}</p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {claim.receipt_file_id ? (
                          <button
                            onClick={() => viewReceipt(claim.receipt_url, claim)}
                            className="group relative w-12 h-12 rounded-lg overflow-hidden border border-slate-200 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
                            title={`View: ${claim.receipt_filename || 'Receipt'}`}
                            data-testid={`thumbnail-${claim.id}`}
                          >
                            {claim.receipt_filename?.toLowerCase().endsWith('.pdf') ? (
                              <div className="w-full h-full flex items-center justify-center bg-red-50">
                                <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                </svg>
                              </div>
                            ) : (
                              <ThumbnailImage 
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/thumbnail/${claim.receipt_file_id}`}
                                alt="Receipt thumbnail"
                              />
                            )}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                              <Eye className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                            </div>
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">No receipt</span>
                        )}
                      </td>
                      <td className="py-3 px-4 font-semibold">{formatCurrency(claim.amount)}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {formatDate(claim.expense_date || claim.created_at)}
                      </td>
                      <td className="py-3 px-4">{getStatusBadge(claim.status)}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {/* View receipt button - for both admin and employees (own claims) */}
                          {claim.receipt_url && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-blue-600"
                              onClick={() => viewReceipt(claim.receipt_url, claim)} 
                              data-testid={`action-view-file-${claim.id}`}
                              title="View Supporting Document"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          )}
                          
                          {/* Employee actions */}
                          {claim.status === 'draft' && !isAdmin && (
                            <Button size="sm" variant="outline" onClick={() => handleSubmitClaim(claim.id)} data-testid={`submit-claim-${claim.id}`}>
                              Submit
                            </Button>
                          )}
                          
                          {/* Admin actions */}
                          {isAdmin && claim.status === 'submitted' && (
                            <>
                              <Button size="sm" variant="outline" className="text-purple-600" onClick={() => openActionDialog(claim, 'verify')} data-testid={`verify-claim-${claim.id}`}>
                                Verify
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => openActionDialog(claim, 'reject')} data-testid={`reject-claim-${claim.id}`}>
                                Reject
                              </Button>
                            </>
                          )}
                          
                          {isAdmin && claim.status === 'verified' && (
                            <>
                              <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => openActionDialog(claim, 'approve')} data-testid={`approve-claim-${claim.id}`}>
                                Approve
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => openActionDialog(claim, 'reject')} data-testid={`reject-claim-${claim.id}`}>
                                Reject
                              </Button>
                            </>
                          )}
                          
                          {isAdmin && claim.status === 'approved' && (
                            <Button size="sm" variant="outline" className="text-green-700" onClick={() => openActionDialog(claim, 'pay')} data-testid={`pay-claim-${claim.id}`}>
                              Mark Paid
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredClaims.length === 0 && (
                <div className="text-center py-12">
                  <Receipt className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No claims found</p>
                  {activeTab !== 'all' && (
                    <p className="text-sm text-slate-400 mt-1">Try selecting "All Claims" or create a new {activeTab} claim</p>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Create Claim Modal */}
        <ClaimCreateModal
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          onSuccess={fetchData}
          isVerifier={isVerifier || (user?.role === 'admin')}
          employees={employees}
        />

        {/* Action Modal */}
        <ClaimActionModal
          isOpen={showAction}
          onClose={() => {
            setShowAction(false);
            setSelectedClaim(null);
            setCurrentAction('');
          }}
          claim={selectedClaim}
          action={currentAction}
          onSuccess={fetchData}
        />

        {/* Upload Receipt Modal */}
        <ClaimUploadModal
          isOpen={showUpload}
          onClose={() => {
            setShowUpload(false);
            setSelectedClaim(null);
          }}
          claim={selectedClaim}
          onSuccess={fetchData}
        />

        {/* Document Viewer Modal */}
        <DocumentViewerModal
          isOpen={showDocViewer}
          onClose={() => setShowDocViewer(false)}
          documentUrl={viewerDoc.url}
          documentName={viewerDoc.name}
          documentType={viewerDoc.type}
        />
      </div>
    </div>
  );
};

export default Claims;
